import { api, fetchWithAuth } from './api';
import type { Account, LogoOption } from '../types';

interface AccountsResponse {
  accounts: Account[];
  total: number;
  page: number;
  per_page: number;
}

export const accountsApi = {
  list: (params?: { niche?: string; search?: string; page?: number; per_page?: number; lang?: string; platform?: string; ids?: number[] }) => {
    const qs = new URLSearchParams();
    if (params?.niche) qs.set('niche', params.niche);
    if (params?.search) qs.set('search', params.search);
    if (params?.page) qs.set('page', params.page.toString());
    if (params?.per_page) qs.set('per_page', params.per_page.toString());
    if (params?.lang) qs.set('lang', params.lang);
    if (params?.platform) qs.set('platform', params.platform);
    if (params?.ids && params.ids.length > 0) qs.set('ids', params.ids.join(','));
    return api.get<AccountsResponse>(`/accounts?${qs}`);
  },
  get: (username: string) => api.get<Account>(`/accounts/${username}`),
  add: (data: { username: string; niche?: string; account_type?: string; platform?: string }) =>
    api.post<{ id: number; username: string; platform: string }>('/accounts', data),
  update: (username: string, data: Partial<Account>) =>
    api.patch(`/accounts/${username}`, data),
  hide: (username: string, reason?: string) =>
    api.post(`/accounts/${username}/hide`, { reason }),
  bulkUpdate: (accountIds: number[], updates: Record<string, unknown>) =>
    api.post('/accounts/bulk-update', { account_ids: accountIds, updates }),

  uploadLogo: async (username: string, file: File): Promise<{ logo_url: string }> => {
    const form = new FormData();
    form.append('file', file);
    const res = await fetchWithAuth(`/api/accounts/${username}/logo/upload`, {
      method: 'POST',
      body: form,
    });
    return res.json();
  },
  removeBackground: (username: string, imageData: string) =>
    api.post<{ image_data: string }>(`/accounts/${username}/logo/remove-background`, { image_data: imageData }),
  enrichLogoPrompt: (username: string) =>
    api.post<{ prompt: string }>(`/accounts/${username}/logo/enrich-prompt`, {}),
  generateLogos: (username: string, prompt: string) =>
    api.post<{ options: LogoOption[] }>(`/accounts/${username}/logo/generate`, { prompt }),
  selectLogo: (username: string, svg: string) =>
    api.post<{ logo_url: string }>(`/accounts/${username}/logo/select`, { svg }),
  deleteLogo: (username: string) =>
    api.delete<{ deleted: boolean }>(`/accounts/${username}/logo`),
};
